import { SavedJob } from "../model/SavedJob.js";
import {composeCheckValidityApiUrl, composeDetailUrlFromUniqueId} from "./urlHandler.js";

/**
 * @type {string[]}
 */
const savedJobsProps = [
    'addTime',
    'companyName',
    'isValid',
    'jobType',
    'location',    // 既存データ用
    'locations',   // 新データ用
    'salary',
    'title',
    'uniqueId',
    'stationName',
    'trainList',
];

/**
 * ローカルストレージからお気に入り求人リストを取得
 *
 * @param {boolean} includeInvalidJobs
 * @return {Object[]}
 */
export const fetchSavedJobs = (includeInvalidJobs = true) => {
    const savedJobsJson = localStorage.getItem('saved_jobs');
    if (savedJobsJson === null) return [];
    const savedJobs = JSON.parse(savedJobsJson);

    if (!Array.isArray(savedJobs)) {
        // savedJobsがarrayでない場合は無効データと見なしてクリーンアップする
        updateSavedJobs([]);
        return [];
    }

    // 古い形式のデータを新しい形式に変換
    const convertedJobs = savedJobs.map(convertToNewFormat);
    updateSavedJobs(convertedJobs);

    return includeInvalidJobs ? convertedJobs : convertedJobs.filter((jobData) => jobData.isValid);
};

/**
 * お気に入り求人のバリデーション
 * 各求人データに対してプロパティのチェックを行う
 *
 * @param {Object[]} savedJobs
 * @return {Object[]} validated saved jobs
 */
const validateSavedJobs = (savedJobs) => {
    return savedJobs.filter((jobData) => {
        // location または locations のどちらかが存在すれば有効とする
        const hasLocation = jobData['location'] !== undefined || jobData['locations'] !== undefined;
        const otherPropsValid = savedJobsProps
            .filter(prop => prop !== 'location' && prop !== 'locations')
            .every((prop) => jobData[prop] !== undefined);
        
        return hasLocation && otherPropsValid;
    });
};

/**
 * ローカルストレージをバリデーション済みデータで更新する
 *
 * @param {Object[]} savedJobs
 */
export const updateSavedJobs = (savedJobs) => {
    const validatedSavedJobs = validateSavedJobs(savedJobs);
    localStorage.setItem('saved_jobs', JSON.stringify(validatedSavedJobs));
};

/**
 * APIでお気に入り求人の掲載確認をし、
 * 掲載情報（isValid）をlocalStorageにセットする
 *
 * @return {Promise<void>}
 */
export const checkValidity = async () => {
    const savedJobs = fetchSavedJobs();
    if (savedJobs.length === 0) return;

    const apiUrl = composeCheckValidityApiUrl(savedJobs);

    try {
        // 掲載確認APIを呼ぶ
        const response = await fetch(apiUrl);
        const result = await response.json();

        // { uniqueId_1: bool, uniqueId_2: bool, ... }
        const validityList = result.validityList;

        // 各お気に入り求人のisValidを更新する
        savedJobs.forEach((savedJobData, i, self) => {
            const uniqueId = savedJobData['uniqueId'];

            if (uniqueId in validityList) {
                // isValidを更新
                savedJobData['isValid'] = validityList[uniqueId];
                // savedJobs自体を更新
                self[i] = savedJobData;
            }
        });

        // isValidが更新されたsavedJobsをローカルストレージにセット
        localStorage.setItem('saved_jobs', JSON.stringify(savedJobs))
    } catch (error) {
        console.log(error);
    }
}

/**
 * 正規化対象の勤務地表記を正規化 USのNeighborhoodの場合
 * Notice: locationはWindowの予約語なので変数名に使用しない
 *
 * @param {string|null} locationDisplay
 * @return {string}
 */
export const normalizeLocation = (locationDisplay) => {
    const normalPattern = /area\)$/;

    // 勤務地が空か、~~area)表記の場合はそのまま返す
    if (!locationDisplay || normalPattern.test(locationDisplay)) {
        return locationDisplay;
    }

    return locationDisplay.replace(/^(.*?), (.*)$/, '$2 ($1 area)');
};

/**
 * いつ保存されたかを表す文言を生成
 *
 * @param savedTime_ms
 * @return {string}
 */
export const generateWhenSavedPhrase = (savedTime_ms, translations) => {
    const hourFromSaved = (Date.now() - savedTime_ms) / (1000 * 60 * 60);

    if (hourFromSaved <= 24) {
        return translations.saved_today;
    } else if (hourFromSaved <= 24 * 30) {
        const dayFromSavedInt = Math.ceil(hourFromSaved / 24);
        return translations.saved_xx_days_ago.replaceAll('xx', dayFromSavedInt);//`Saved ${dayFromSavedInt} days ago`;
    } else {
        return translations.saved_xx_days_ago.replaceAll('xx', '30+');//'Saved 30+ days ago';
    }
};

/**
 * ハンバーガーメニュー内のお気に入り求人リストとヘッダーバッジをレンダリング
 * Notice: ローカルストレージの値を表示する際は、
 *         attr()やtext()などエスケープ処理を内包した関数を使用する
 *
 * @return {void}
 */
export const renderSideNavSavedJobs = () => {
    const savedJobs = fetchSavedJobs(false);
    const jobCount = savedJobs.length;

    // お気に入り求人件数表示
    let savedJobsCountText = $('#s-sidenav_savedJobsCount').text();
    savedJobsCountText = savedJobsCountText.replace(/\s*\(\d+\)$/, ''); // 既存の件数部分を削除
    $('#s-sidenav_savedJobsCount').text(`${savedJobsCountText} (${jobCount})`);

    // ヘッダーのお気に入りバッジも更新
    updateHeaderFavoriteBadge();

    // リスト初期化
    const $savedJobsList = $('#s-sidenav_savedJobsList');
    $savedJobsList.empty();

    if (jobCount === 0) return;

    // 最大3件表示
    for (let job of savedJobs.splice(0, 3)) {
        // リンク生成・タイトル表示
        const $detailLink = $('<a class="p-sidenav_content_saved p-sidenav_content_cell_link" rel="nofollow"></a>')
            .attr('href', composeDetailUrlFromUniqueId(job.uniqueId))
            .text(shortenDisplayText(job.title));

        // 会社名表示
        if (job.companyName) {
            const $companyName = $('<span class="p-sidenav_content_savedCompany"></span>')
                .text(shortenDisplayText(job.companyName));
            $detailLink.append($companyName);
        }

        // リストの生成
        const $listItem = $('<li></li>').append($detailLink);
        $savedJobsList.append($listItem);

        // クリック計測
        $listItem.on('click', () => {
            const savedJobs = fetchSavedJobs();
            const postedAt = generateWhenSavedPhrase(job.addTime);
            const position = savedJobs.findIndex((savedJob) => savedJob.uniqueId === job.uniqueId) + 1;

            analytics.clickSideNavigationSavedJob(job, postedAt, position);
        });
    }
};

/**
 * ヘッダーのお気に入りバッジを初期化
 * @return {void}
 */
export const initHeaderFavoriteBadge = () => {
    updateHeaderFavoriteBadge();
};

/**
 * ヘッダーのお気に入りバッジを更新
 * @return {void}
 */
export const updateHeaderFavoriteBadge = () => {
    const savedJobs = fetchSavedJobs(false);
    const jobCount = savedJobs.length;
    const $badge = $('.s-saved_jobs_count');

    if (jobCount === 0) {
        // 0件の場合はバッジを非表示
        $badge.hide();
    } else {
        // 1件以上の場合はバッジを表示し、件数を表示
        $badge.text(jobCount).show();
    }
};

/**
 * 50文字を超える場合は50文字以内に出現する最後の半角スペース以降を...に置換
 *
 * @param {string} text
 * @return {string}
 */
const shortenDisplayText = (text) => {
    if (text.length <= 50) return text;

    const shortText = text.substring(0, 50);
    return shortText.replace(/ [^ ]*$/, '...');
};

/**
 * 0件表示
 *
 * @return {void}
 */
export const showZeroView = () => {
    $('.s-nothingToShow').show();
    // 全削除ボタン非表示
    $('.s-detailAllWrapper').hide();
};

/**
 * 対象のお気に入り求人を削除
 *
 * @param {string} uniqueId
 */
export const removeSavedJob = (uniqueId) => {
    const savedJobs = fetchSavedJobs();

    // お気に入りから対象jobを削除
    const updatedSavedJobs = savedJobs.filter((jobData) => jobData['uniqueId'] !== uniqueId);
    localStorage.setItem('saved_jobs', JSON.stringify(updatedSavedJobs));
};

/**
 * お気に入り求人を全て削除
 *
 * @return {boolean}
 */
export const removeAllSavedJobs = () => {
    const isOk = window.confirm('Are you sure you want to delete all saved jobs?');
    if (!isOk) return false;

    localStorage.setItem('saved_jobs', JSON.stringify([]));
    return true;
};

/**
 * お気に入り削除後、画面下部に削除した旨を通知
 */
export const showAlert = () => {
    $('.s-alert').removeClass('is-hide');

    // 3sで消える
    setTimeout(() => {
        $('.s-alert').addClass('is-hide');
    }, 3000);
};

/**
 * 求人がお気に入り登録されているか
 *
 * @param {string} uniqueId
 * @return {boolean}
 */
export const isSavedJob = (uniqueId) => {
    const savedJobs = fetchSavedJobs();
    return savedJobs.some(savedJob => savedJob.uniqueId === uniqueId);
}

/**
 * 閲覧履歴からお気に入り登録
 *
 * @param {ViewedJob} viewedJob
 * @return {void}
 */
export const saveViewedJob = (viewedJob) => {
    // 既に保存されている場合
    if (isSavedJob(viewedJob.uniqueId)) {
        return;
    }

    const savedJobs = fetchSavedJobs();
    const savedJob = new SavedJob(viewedJob);

    // お気に入りが30件を超えないように削除
    while (savedJobs.length >= 30) {
        savedJobs.pop();
    }

    // お気に入りリストにjobデータを追加
    savedJobs.unshift(savedJob);
    localStorage.setItem('saved_jobs', JSON.stringify(savedJobs));
}

/**
 * 古い形式のデータを新しい形式に変換する
 * 
 * @param {Object} jobData
 * @return {Object}
 */
const convertToNewFormat = (jobData) => {
    if (jobData['location'] !== undefined && !jobData['locations']) {
        // locationがnullの場合は空配列を設定
        jobData['locations'] = jobData['location'] ? [jobData['location']] : [];
        delete jobData['location'];
    }
    // locationsがnullの場合は空配列に変換
    if (jobData['locations'] === null) {
        jobData['locations'] = [];
    }
    return jobData;
};
